import {
  ListScrollLocation,
  useVirtuosoMethods,
} from "@virtuoso.dev/message-list";
import colors from "app/colors";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import { ArrowDownIcon } from "modules/icons";
import React, { memo, useCallback } from "react";

interface IProps {
  location: ListScrollLocation;
  hasLastMessage: boolean;
  hasFirstMessage: boolean;
  initializeMessageList: (startAtTop: boolean) => void;
}

const ScrollToEdgeButton = ({
  location,
  hasLastMessage,
  hasFirstMessage,
  initializeMessageList,
}: IProps) => {
  const isAtBottom = location.isAtBottom;
  const isAtTop = location.listOffset === 0;

  const { scrollToItem } = useVirtuosoMethods();

  const onScrollToBottomClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (hasLastMessage) {
        scrollToItem({ index: "LAST", align: "end", behavior: "auto" });
        return;
      }
      initializeMessageList(false);
    },
    [hasLastMessage, initializeMessageList, scrollToItem],
  );

  const onScrollToTopClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (hasFirstMessage) {
        scrollToItem({ index: 0, align: "start", behavior: "auto" });
        return;
      }
      initializeMessageList(true);
    },
    [hasFirstMessage, initializeMessageList, scrollToItem],
  );

  if (isAtBottom && isAtTop) {
    return null;
  }

  if (isAtBottom) {
    return (
      <CircularIconButton
        variant="hover"
        aria-label="to top of message list"
        onClick={onScrollToTopClicked}
        boxShadow="xl"
        bg={colors.white}
        icon={
          <SyncoTooltip label="Scroll To Top">
            <ArrowDownIcon
              fill={colors.gray30}
              boxSize={8}
              transform="rotate(180deg)"
            />
          </SyncoTooltip>
        }
      />
    );
  }

  return (
    <CircularIconButton
      variant="hover"
      aria-label="to bottom of message list"
      onClick={onScrollToBottomClicked}
      boxShadow="xl"
      bg={colors.white}
      icon={
        <SyncoTooltip label="Scroll To Bottom">
          <ArrowDownIcon fill={colors.gray30} boxSize={8} />
        </SyncoTooltip>
      }
    />
  );
};

export default memo(ScrollToEdgeButton);
